/**
 * UC-008 Tax Document upload schemas (Phase 22).
 *
 * Type contracts pro `/api/admin/months/{budgetId}/tax-document` POST endpoint
 * (multipart/form-data — viz `backend/api/.../TaxDocumentRoutes.kt`).
 *
 * Per DEC-011 (anglické technické pojmenování) + REQ-API-012 (uniform error
 * format). Princip #1 — žádný PII v emailu Davidovi (jen link na portál).
 */

import { z } from "zod";

export const VAT_RATES = ["standard_21", "zero", "not_subject"] as const;
export type VatRate = (typeof VAT_RATES)[number];

/** Form values v React Hook Form (před multipart serialization).
 *
 * `file` here is `File` (Zod schema enforces presence; default null v form
 * state je převedený na valid File při submission přes refine). RHF resolver
 * Zod returns parsed output type, takže `file` je `File` post-validation.
 */
export interface TaxDocumentUploadFormValues {
  file: File;
  document_number: string;
  issue_date: string; // ISO yyyy-MM-dd
  amount_kc: number;
  vat_rate: VatRate;
  note: string;
  /** Pouze pro EC6 re-upload — vyžadované při `existing_tax_document_id != null`. */
  reason: string;
}

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Zod schema — UI-level validation pre-submit.
 *
 * Note: server přepočítá vše per 10-step pipeline (princip #2 — neoptimistic
 * client trust); UI validation je jen UX (rychlejší feedback, snížení 422 round-tripů).
 *
 * Zod 4 API: `error` message vs older `errorMap`/`invalid_type_error`.
 */
export const taxDocumentUploadSchema = z.object({
  file: z
    .instanceof(File, { message: "PDF soubor je povinný" })
    .refine((f) => f.size > 0, { message: "Soubor je prázdný" })
    .refine((f) => f.size <= MAX_FILE_BYTES, {
      message: `Soubor je větší než 10 MB (${MAX_FILE_BYTES.toLocaleString("cs-CZ")} B)`,
    })
    .refine((f) => f.type === "application/pdf", {
      message: "Pouze application/pdf typ",
    }),
  document_number: z
    .string()
    .trim()
    .min(1, { message: "Číslo dokladu je povinné" })
    .max(64, { message: "Maximálně 64 znaků" }),
  issue_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Formát yyyy-MM-dd" })
    .refine((iso) => Date.parse(iso) <= Date.now(), {
      message: "Datum vystavení nesmí být v budoucnosti",
    }),
  amount_kc: z
    .number({ message: "Celé kladné číslo Kč" })
    .int({ message: "Celé číslo Kč" })
    .positive({ message: "Větší než 0" }),
  vat_rate: z.enum(VAT_RATES, { message: "Vyber sazbu DPH" }),
  note: z.string().max(500, { message: "Maximálně 500 znaků" }).optional().default(""),
  reason: z
    .string()
    .max(500, { message: "Maximálně 500 znaků" })
    .optional()
    .default(""),
});

export type TaxDocumentUploadInput = z.infer<typeof taxDocumentUploadSchema>;

/** Server response — happy path (Created / OK). */
export interface TaxDocumentUploadResponse {
  tax_document_id: string;
  stored_file_id: string;
  version_number: number;
  attachments_count: number;
  attachments_advisory: string | null;
  re_upload: boolean;
  sha256: string;
  size_bytes: number;
}

/** Error codes from server (REQ-API-012 enum). */
export const TAX_DOC_ERROR_CODES = {
  INVALID_CONTENT_TYPE: "INVALID_CONTENT_TYPE",
  INVALID_MAGIC_BYTES: "INVALID_MAGIC_BYTES",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  PDF_PARSE_FAILED: "PDF_PARSE_FAILED",
  MALWARE_DETECTED: "MALWARE_DETECTED",
  MALWARE_SCAN_UNAVAILABLE: "MALWARE_SCAN_UNAVAILABLE",
  BUDGET_NOT_FOUND: "BUDGET_NOT_FOUND",
  BUDGET_NOT_APPROVED: "BUDGET_NOT_APPROVED",
  DUPLICATE_DOCUMENT_NUMBER: "DUPLICATE_DOCUMENT_NUMBER",
  DUPLICATE_CONTENT: "DUPLICATE_CONTENT",
  INVALID_ISSUE_DATE: "INVALID_ISSUE_DATE",
  INVALID_AMOUNT: "INVALID_AMOUNT",
  INVALID_VAT_RATE: "INVALID_VAT_RATE",
  MISSING_FILE: "MISSING_FILE",
  MISSING_FIELD: "MISSING_FIELD",
} as const;
export type TaxDocErrorCode = (typeof TAX_DOC_ERROR_CODES)[keyof typeof TAX_DOC_ERROR_CODES];

/** Builds FormData payload pro multipart POST. */
export function buildUploadFormData(
  values: TaxDocumentUploadFormValues,
  tenantId: string,
): FormData {
  const fd = new FormData();
  if (values.file) fd.append("file", values.file, values.file.name);
  fd.append("document_number", values.document_number);
  fd.append("issue_date", values.issue_date);
  fd.append("amount_kc", String(values.amount_kc));
  fd.append("vat_rate", values.vat_rate);
  if (values.note) fd.append("note", values.note);
  fd.append("tenant_id", tenantId);
  return fd;
}
